import Vue from 'vue';

type ToastID = string | number;

export interface GameToastOptions {
  /** When false, the message is shown as-is. Default: true */
  translate?: boolean;
  timeout?: number | false;
  position?: string;
  closeOnClick?: boolean;
  hideProgressBar?: boolean;
  [key: string]: unknown;
}

function getToastApi() {
  return Vue.$toast;
}

function resolveMessage(message: string, translate = true): string {
  if (!translate || !message) {
    return message;
  }

  return Vue.i18n.translate(message) || message;
}

function withResolvedMessage(
  method: (content: string, options?: any) => ToastID,
  message: string,
  options?: GameToastOptions
): ToastID {
  const { translate = true, ...toastOptions } = options || {};

  return method(resolveMessage(message, translate), toastOptions);
}

/**
 * Reusable toast helper with vuex-i18n support.
 *
 * @example
 * // In a component
 * this.$showToast.success('Bet placed');
 *
 * @example
 * // In Vuex / axios (no component context)
 * import { showToast } from '@/utils/toast';
 * showToast.error('NETWORK_ERROR_MESSAGE');
 *
 * @example
 * // Dynamic message without i18n lookup
 * showToast.error(`${err.data.message} E${err.data.code}`, { translate: false });
 */
export const showToast = {
  show(message: string, options?: GameToastOptions): ToastID {
    return withResolvedMessage(getToastApi(), message, options);
  },

  success(message: string, options?: GameToastOptions): ToastID {
    return withResolvedMessage(getToastApi().success, message, options);
  },

  info(message: string, options?: GameToastOptions): ToastID {
    return withResolvedMessage(getToastApi().info, message, options);
  },

  error(message: string, options?: GameToastOptions): ToastID {
    return withResolvedMessage(getToastApi().error, message, options);
  },

  warning(message: string, options?: GameToastOptions): ToastID {
    return withResolvedMessage(getToastApi().warning, message, options);
  },

  dismiss(id: ToastID): void {
    getToastApi().dismiss(id);
  },

  clear(): void {
    getToastApi().clear();
  },
};

export default showToast;
